/// <amd-module name="VSS/Diagnostics/Dialog/DiagnosticsDialog" />
import React = require("react");
import { ClientContribution as Contribution } from "VSS/Platform/Contracts/Contributions";
import { Components, IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ContributionLink, ContributionNode, DiagnosticsContributionData } from "VSS/Diagnostics/Dialog/DiagnosticsDialog.types";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IContributionDiagnostics, IDiagnosticData } from "VSS/Platform/Page";
import { Dialog } from "VSSUI/Dialog";
import {
    PivotBar,
    PivotBarItem,
    PivotBarItemDeselectionBehavior,
    PivotBarViewActionType,
    IChoiceGroupViewActionProps,
    IOnOffViewActionProps,
    IPivotBarViewAction
} from "VSSUI/PivotBar";
import { PerfDiagnosticsListPivot } from "VSS/Diagnostics/Dialog/DiagnosticsListPivot";
import { PerfDiagnosticsGraph } from "VSS/Diagnostics/Dialog/DiagnosticsGraph";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ViewOptions } from "VSSUI/Utilities/ViewOptions";
import { DiagnosticsScriptGraph } from "./DiagnosticsScriptsGraph";
import { PerfDiagnosticsContentsTree } from "VSS/Diagnostics/Dialog/DiagnosticsContentsTree";

interface PerfDiagnosticsProps extends IVssComponentProperties {
    diagnostics: IDiagnosticData;
    compiledDiagnostics: IDiagnosticData;
    onDismiss: () => any;
}

interface PerfDiagnosticsState extends IVssComponentProperties {
    selectedPivot: string;
    includedContributions: ContributionNode[];
    includedAndConstrainedContributions: ContributionNode[];
    allContributions: Contribution[];
    chosenContributions: string;
    fps: boolean;
    relationships: ContributionLink[];
    displayedNavigation: string[];
    viewOptions: ViewOptions;
}

const enum ContributionsViews {
    OnlyIncluded = "includedContributions",
    InludedAndConstrainedOut = "constrainedOut",
    AllContributions = "allContributions"
}

const enum ScriptViews {
    Chart = "scriptsChart",
    Tree = "scriptsTree"
}

interface ViewChoices {
    key: string;
    text: string;
}

class PerfDiagnosticsDialog extends VssComponent<PerfDiagnosticsProps, PerfDiagnosticsState> {
    targets: { [contributionId: string]: ContributionLink[] };
    includes: { [contributionId: string]: ContributionLink[] };
    targetsFPS: { [contributionId: string]: ContributionLink[] };
    includesFPS: { [contributionId: string]: ContributionLink[] };
    viewChoices: ViewChoices[];
    scriptsViewChoices: ViewChoices[];

    constructor(props: PerfDiagnosticsProps, context: IVssComponentContext) {
        super(props, context);

        const viewOptions = new ViewOptions();
        viewOptions.subscribe(this.changeViewOptions);

        this.targets = {};
        this.includes = {};
        this.targetsFPS = {};
        this.includesFPS = {};

        this.viewChoices = [
            { key: ContributionsViews.OnlyIncluded, text: "Only Included Contributions" },
            { key: ContributionsViews.InludedAndConstrainedOut, text: "Included and Constrained out Contributions" },
            { key: ContributionsViews.AllContributions, text: "All Contributions" }
        ];

        this.scriptsViewChoices = [{ key: ScriptViews.Chart, text: "Dependency Chart" }, { key: ScriptViews.Tree, text: "Dependency Tree" }];

        this.state = {
            includedContributions: [],
            includedContributionsFPS: [],
            includedAndConstrainedContributions: [],
            includedAndConstrainedContributionsFPS: [],
            allContributions: [],
            chosenContributions: ContributionsViews.OnlyIncluded,
            fps: false,
            relationships: [],
            selectedPivot: "list",
            displayedNavigation: [],
            viewOptions
        };
    }

    public render(): JSX.Element {
        const {
            includedContributions,
            selectedPivot,
            includedAndConstrainedContributions,
            fps,
            includedAndConstrainedContributionsFPS,
            allContributions,
            relationships,
            displayedNavigation,
            viewOptions,
            chosenContributions
        } = this.state;

        let viewActions: IPivotBarViewAction[] = [];
        if (selectedPivot === "list") {
            viewActions = [
                {
                    key: "items",
                    name: "Contribution Views",
                    important: true,
                    actionType: PivotBarViewActionType.ChoiceGroup,
                    actionProps: {
                        defaultSelectedKey: ContributionsViews.OnlyIncluded,
                        options: allContributions.length ? this.viewChoices : this.viewChoices.slice(0, 2)
                    } as IChoiceGroupViewActionProps
                },
                {
                    key: "FPSHistory",
                    name: "FPS History or Current Page Contributions",
                    important: false,
                    actionType: PivotBarViewActionType.OnOff,
                    actionProps: {
                        onText: "FPS History",
                        offText: "Current Page"
                    } as IOnOffViewActionProps
                }
            ];
        } else if (selectedPivot === "scripts") {
            viewActions = [
                {
                    key: "items",
                    name: "Views",
                    important: true,
                    actionType: PivotBarViewActionType.ChoiceGroup,
                    actionProps: {
                        defaultSelectedKey: ScriptViews.Chart,
                        options: this.scriptsViewChoices
                    } as IChoiceGroupViewActionProps
                }
            ];
        }

        return (
            <Dialog
                onDismiss={this.props.onDismiss}
                calloutContentClassName="performance-dialog dialog-full-size dialog--diagnostics"
                lightDismiss={false}
                showCloseButton={true}
                titleProps={{ text: "Diagnostics" }}
            >
                <PivotBar
                    className="flex-grow flex"
                    isPivotBarContentScrollable={false}
                    selectedPivot={selectedPivot}
                    onPivotClicked={this.onPivotChanged}
                    viewOptions={viewOptions}
                    viewActions={viewActions}
                >
                    <PivotBarItem itemKey="list" name="List" className="flex flex-grow flex-column">
                        {includedAndConstrainedContributions.length > 0 ? (
                            <PerfDiagnosticsListPivot
                                contributions={fps ? includedAndConstrainedContributionsFPS : includedAndConstrainedContributions}
                                allContributions={allContributions}
                                chosenContributions={this.getChosenContributions()}
                                targets={fps ? this.targetsFPS : this.targets}
                                includes={fps ? this.includesFPS : this.includes}
                            />
                        ) : (
                            <Spinner size={SpinnerSize.large} label="Loading contributions..." />
                        )}
                    </PivotBarItem>
                    <PivotBarItem itemKey="graph" name="Graph" className="flex" deselectionBehavior={PivotBarItemDeselectionBehavior.Hide}>
                        {includedAndConstrainedContributions.length > 0 ? (
                            <PerfDiagnosticsGraph
                                contributions={includedAndConstrainedContributions}
                                relationships={relationships}
                                displayedNavigation={displayedNavigation}
                            />
                        ) : (
                            <Spinner size={SpinnerSize.large} label="Loading contributions..." />
                        )}
                    </PivotBarItem>
                    <PivotBarItem itemKey="scripts" name="Scripts" className="flex diag-dialog-full-height-width" deselectionBehavior={PivotBarItemDeselectionBehavior.Hide}>
                        {includedContributions.length > 0 ? (
                            chosenContributions !== ScriptViews.Tree ? (
                                <DiagnosticsScriptGraph contributions={includedContributions} relationships={relationships} />
                            ) : (
                                <PerfDiagnosticsContentsTree contributions={includedContributions} relationships={relationships} />
                            )
                        ) : (
                            <Spinner size={SpinnerSize.large} label="Loading contributions..." />
                        )}
                    </PivotBarItem>
                </PivotBar>
            </Dialog>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();

        const requestDiagnostics: IContributionDiagnostics = this.props.diagnostics!.contributions!["request"][0];
        this.setUpContributions(requestDiagnostics, false);
        const requestDiagnosticsFPS: IContributionDiagnostics = this.props.compiledDiagnostics!.contributions!["request"][0];
        this.setUpContributions(requestDiagnosticsFPS, true);
    }

    public componentWillUnmount() {
        this.state.viewOptions.unsubscribe(this.changeViewOptions);
    }
    private setUpContributions(requestDiagnostics: IContributionDiagnostics, fps: boolean) {
        const contributionsByName: { [contributionId: string]: ContributionNode } = {};
        const contributions: ContributionNode[] = [];
        const relationships: ContributionLink[] = [];

        // Build the set of all visible contributions.
        for (let contributionId in requestDiagnostics.contributions) {
            contributionsByName[contributionId] = Object.assign(
                {
                    size: 5
                },
                requestDiagnostics.contributions[contributionId]
            );
            contributions.push(contributionsByName[contributionId]);
        }

        for (let parentContributionId in requestDiagnostics.relationships) {
            if (parentContributionId !== "") {
                const childEvaluations = requestDiagnostics.relationships[parentContributionId];
                for (let childEvaluation of childEvaluations) {
                    relationships.push(
                        Object.assign(
                            {
                                source: contributionsByName[childEvaluation.parentContributionId],
                                target: contributionsByName[childEvaluation.childContributionId]
                            },
                            childEvaluation
                        )
                    );
                }
            }
        }

        // Get the set of navigation elements that are displayed. We will make them larger.
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const displayedNavigation = navigationService.getDisplayedNavigation();

        for (let contributionId of displayedNavigation) {
            contributionsByName[contributionId].size = 10;
        }
        if (fps) {
            relationships.forEach(element => {
                if (!this.includesFPS[element.parentContributionId]) {
                    this.includesFPS[element.parentContributionId] = [element];
                } else {
                    this.includesFPS[element.parentContributionId].push(element);
                }
                if (!this.targetsFPS[element.childContributionId]) {
                    this.targetsFPS[element.childContributionId] = [element];
                } else {
                    this.targetsFPS[element.childContributionId].push(element);
                }
            });

            const newItems = contributions.filter(item => {
                return this.isActiveContributionFPS(item.id);
            });
            this.setState({
                includedContributionsFPS: newItems,
                includedAndConstrainedContributionsFPS: contributions
            });
        } else {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync("ms.vss-features.diagnostics-contribution-data").then((value: DiagnosticsContributionData) => {
                if (value) {
                    this.setState({
                        allContributions: value.allContributions
                    });
                }
            });
            relationships.forEach(element => {
                if (!this.includes[element.parentContributionId]) {
                    this.includes[element.parentContributionId] = [element];
                } else {
                    this.includes[element.parentContributionId].push(element);
                }
                if (!this.targets[element.childContributionId]) {
                    this.targets[element.childContributionId] = [element];
                } else {
                    this.targets[element.childContributionId].push(element);
                }
            });

            const newItems = contributions.filter(item => {
                return this.isActiveContribution(item.id);
            });
            this.setState({
                includedContributions: newItems,
                includedAndConstrainedContributions: contributions,
                relationships: relationships,
                displayedNavigation: displayedNavigation
            });
        }
    }

    private onPivotChanged = (ev: React.MouseEvent<HTMLElement>, pivotKey: string) => {
        this.setState({
            selectedPivot: pivotKey
        });
    };

    private changeViewOptions = () => {
        this.setState({
            chosenContributions: this.state.viewOptions.getViewOption("items"),
            fps: this.state.viewOptions.getViewOption("FPSHistory")
        });
    };

    private getChosenContributions() {
        const {
            includedContributions,
            includedAndConstrainedContributions,
            allContributions,
            chosenContributions,
            includedContributionsFPS,
            includedAndConstrainedContributionsFPS,
            fps
        } = this.state;
        if (chosenContributions === ContributionsViews.AllContributions) {
            return allContributions;
        }
        if (fps) {
            if (chosenContributions === ContributionsViews.InludedAndConstrainedOut) {
                return includedAndConstrainedContributionsFPS;
            }
            return includedContributionsFPS;
        } else {
            if (chosenContributions === ContributionsViews.InludedAndConstrainedOut) {
                return includedAndConstrainedContributions;
            }
            return includedContributions;
        }
    }

    private isActiveContribution(id: string): boolean {
        if (!this.targets[id]) {
            return true;
        }
        return this.targets[id].some(target => target.outputOptions !== 0);
    }

    private isActiveContributionFPS(id: string): boolean {
        if (!this.targetsFPS[id]) {
            return true;
        }
        return this.targetsFPS[id].some(target => target.outputOptions !== 0);
    }
}

Components.add("vss.perf-diagnostics-dialog", PerfDiagnosticsDialog);
